/**
 * KI-Übersetzung TP1 — gespeicherte Inhalte in DE/EN/PL.
 * Backend liefert `content_translations` am v1-User (GET /admin/user/{id})
 * und am v2-Member-Detail. Das Original bleibt im jeweiligen Feld.
 */
import { apiV2 } from './api';

export type Lang = 'de' | 'en' | 'pl';
export const LANGS: Lang[] = ['de', 'en', 'pl'];

export interface TranslationEntry {
  text: string | null;
  origin: 'source' | 'extracted' | 'machine' | 'manual';
  status: 'ok' | 'pending' | 'failed' | 'stale';
  source_language: string | null;
}
export type FieldTranslations = Partial<Record<Lang, TranslationEntry>>;
export type TranslatableType = 'profile' | 'user';
export type ContentTranslations = Partial<Record<TranslatableType, Record<string, FieldTranslations>>>;

export interface ResolvedTranslation {
  text: string;
  lang: Lang | null;
  state: 'original' | 'translated' | 'pending' | 'failed' | 'stale' | 'none';
  entry?: TranslationEntry;
  sourceLanguage: Lang | null;
}

const asLang = (v: string | null | undefined): Lang | null =>
  v && (LANGS as string[]).includes(v) ? (v as Lang) : null;

export function resolveTranslation(
  entries: FieldTranslations | undefined,
  uiLang: string,
  original: string | null | undefined,
): ResolvedTranslation {
  const orig = original ?? '';
  const lang = asLang(uiLang.slice(0, 2)) ?? 'de';
  if (!orig.trim() || !entries || Object.keys(entries).length === 0) {
    return { text: orig, lang: null, state: 'none', sourceLanguage: null };
  }
  const entry = entries[lang];
  const sourceLanguage = asLang(Object.values(entries).find(e => e?.source_language)?.source_language ?? null);
  if (!entry) return { text: orig, lang: null, state: 'none', sourceLanguage };
  // Status vor Herkunft prüfen: Eine pending/failed-Zeile (auch source/extracted) trägt noch
  // den Text des alten Originals — angezeigt wird dann immer das aktuelle Original.
  if (entry.status === 'pending') return { text: orig, lang: null, state: 'pending', entry, sourceLanguage };
  if (entry.status === 'failed') return { text: orig, lang: null, state: 'failed', entry, sourceLanguage };
  // "source" ist per Definition das Original → immer das aktuelle Feld anzeigen, nie entry.text.
  if (entry.origin === 'source') return { text: orig, lang, state: 'original', entry, sourceLanguage };
  if (entry.origin === 'extracted') {
    return { text: entry.status === 'ok' ? (entry.text ?? orig) : orig, lang, state: 'original', entry, sourceLanguage };
  }
  if (!entry.text) return { text: orig, lang: null, state: 'none', entry, sourceLanguage };
  return { text: entry.text, lang, state: entry.status === 'stale' ? 'stale' : 'translated', entry, sourceLanguage };
}

interface Envelope<T> { data: T }

export const contentTranslationsApi = {
  get: async (type: TranslatableType, id: number): Promise<Record<string, FieldTranslations>> =>
    (await apiV2.get<Envelope<Record<string, FieldTranslations>>>(`/admin/translations/${type}/${id}`)).data.data,
  update: async (type: TranslatableType, id: number, field: string, lang: Lang, text: string): Promise<FieldTranslations> =>
    (await apiV2.put<Envelope<FieldTranslations>>(`/admin/translations/${type}/${id}/${field}/${lang}`, { text })).data.data,
  keep: async (type: TranslatableType, id: number, field: string, lang: Lang): Promise<FieldTranslations> =>
    (await apiV2.post<Envelope<FieldTranslations>>(`/admin/translations/${type}/${id}/${field}/${lang}/keep`)).data.data,
  retranslate: async (
    type: TranslatableType, id: number, fields?: string[], languages?: Lang[],
  ): Promise<{ queued: boolean }> =>
    (await apiV2.post<Envelope<{ queued: boolean }>>(`/admin/translations/${type}/${id}/retranslate`, {
      ...(fields ? { fields } : {}),
      ...(languages ? { languages } : {}),
    })).data.data,
};
